<template>
  <el-card class="lesson-list-box">
    <div slot="header" class="clearfix" style="margin-bottom: 15px;">
      <div style="float: left;">
        <span>Test Lesson URLs</span> <br />
        <p style="font-size: 11px;">
          {{ $t('triallesson.list.pageExpires') }}: {{ formatDateTime(currentUser().pageExpires) }}
        </p>
      </div>

      <el-button type="primary" class="generate" :disabled="lessonLeft == 0" @click="generateLesson">
        {{ $t('triallesson.list.generate') }}
      </el-button>
    </div>

    <div :class="messageClass">
      <h1>{{ message }}</h1>
    </div>

    <perfect-scrollbar>
      <datatable :columns="columns" :data="rows" class="lesson-list">
        <template slot-scope="{ row }">
          <tr v-if="lessonsTotal > 0">
            <td :data-label="columns[0].label" >
              <div :class="linkContainerClass">
                <a :href="row.url_student_a" target="_blank" class="link">
                  <span v-if="!isMobileSize">{{ row.url_student_a }}</span>
                  <span v-else>Visit link</span>
                </a>

                <el-tooltip
                  class="item"
                  effect="dark"
                  content="Copy Link to Clipboard"
                  placement="top"
                >
                  <i
                    class="el-icon-copy-document"
                    @click="copy"
                    :class="copyClass"
                    :href="row.url_student_a"
                  ></i>
                </el-tooltip>              
              </div>
            </td>
            <td :data-label="columns[1].label">
              <div :class="linkContainerClass">
                <a :href="row.url_student_b" target="_blank" class="link">
                  <span v-if="!isMobileSize">{{ row.url_student_b }}</span>
                  <span v-else>Visit link</span>
                </a>
                <el-tooltip
                  class="item"
                  effect="dark"
                  content="Copy Link to Clipboard"
                  placement="top"
                >
                  <i
                    class="el-icon-copy-document"
                    @click="copy"
                    :class="copyClass"
                    :href="row.url_student_b"
                  ></i>
                </el-tooltip>
              </div>
            </td>
            <td :data-label="columns[2].label">{{ formatDateTime(row.from) }}</td>
            <td :data-label="columns[3].label">{{ formatDateTime(row.to) }}</td>
          </tr>

          <tr v-if="lessonsTotal <= 0 && !isMobileSize">
            <td colspan="4" class="nodata">{{ row.noData }}</td>
          </tr>
        </template>
      </datatable>
    </perfect-scrollbar>

    <el-dialog :title="$t('triallesson.dialog.warning')" :visible.sync="showError" width="30%" center>
      <span>{{ error }}</span>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="showError = false; error = null;">
          {{ $t('triallesson.dialog.buttons.confirm') }}
        </el-button>
      </span>
    </el-dialog>
  </el-card>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { generateCount } from '@/utils/constants';
import {
  dateTimeFormattingFunc,
  store,
  redirectToLessonGeneratorLogin,
  copyText
} from '@/utils/common';

export default {
  data() {
    return {
      columns: [
        { label: this.$t('triallesson.list.columns.userAUrl') },
        { label: this.$t('triallesson.list.columns.userBUrl') },
        { label: this.$t('triallesson.list.columns.from') },
        { label: this.$t('triallesson.list.columns.to') }
      ],
      error: null,
      showError: false,
      isMobileSize: window.innerWidth < 960
    };
  },
  computed: {
    ...mapState('triallesson', [
      'pageUrl',
      'trialLessonLists',
      'isTrialLessonCreated'
    ]),
    lessonsTotal() {
      return Object.keys(this.trialLessonLists).length;
    },
    lessonLeft() {
      return generateCount - Object.keys(this.trialLessonLists).length;
    },
    copyClass() {
      return !this.isMobileSize ? 'copy_icon' : 'copy_icon_sp';
    },
    messageClass() {
      let msgClass = this.isMobileSize && this.lessonsTotal <= 0 ? 'list-message-sp' : 'list-message';

      if (this.lessonsTotal >= 10) {
        msgClass += ' text-red';
      }

      return msgClass;
    },
    linkContainerClass() {
      return this.isMobileSize ? null : 'link-container';
    },
    message() {
      if (this.lessonsTotal >= 10) {
        return this.$t('triallesson.warnings.usedLesson');
      }

      if (this.lessonsTotal < 10 && this.lessonsTotal > 0) {
        return `${this.$t('triallesson.warnings.remainingTestLesson')}: 残り${this.lessonLeft}回`;
      }

      return this.$t('triallesson.warnings.noData');
    },
    rows() {
      if (this.lessonsTotal <= 0) {
        return [{ noData: this.$t('triallesson.list.noData') }];
      }

      const baseUrl = window.location.origin + process.env.VUE_APP_BASE_PATH;
      return this.trialLessonLists.map(item => ({
        ...item,
        url_student_a: baseUrl + item.url_student_a,
        url_student_b: baseUrl + item.url_student_b
      }));
    }
  },
  mounted() {
    if (this.currentUser() === null) {
      redirectToLessonGeneratorLogin();
    }

    window.addEventListener('resize', () => {
      this.isMobileSize = window.innerWidth < 960;
    });
  },
  watch: {
    error: function(value) {
      this.showError = value !== null;
    }
  },
  methods: {
    ...mapActions('triallesson', ['getTrialLessonLists', 'createLesson']),
    currentUser: function() {
      return JSON.parse(store.getLessonGeneratorCredentials(this.pageUrl));
    },
    formatDateTime: function(dateTime) {
      let formatDateTime = dateTimeFormattingFunc('sa');
      return formatDateTime(dateTime);
    },
    copy: function(e) {
      const url = e.srcElement.getAttribute('href');
      const success = copyText(url);

      if (success) {
        this.$message({
          message: this.$t('triallesson.list.urlCopied'),
          type: 'success'
        });
      } else {
        this.$message.error(this.$t('triallesson.list.urlCopyError'));
      }
    },
    generateLesson: async function() {
      if (this.currentUser() === null) {
        this.error = this.$t('triallesson.warnings.notLoggedIn');
        location.reload();
        return false;
      }

      try {
        await this.createLesson(this.currentUser());
        if (!this.isTrialLessonCreated) {
          this.error = this.$t('triallesson.warnings.notAllowed');
          return false;
        }

        await this.getTrialLessonLists({
          ...this.currentUser(),
          serviceId: this.$route.params.serviceId
        });
      } catch (error) {
        if (error.graphQLErrors && error.graphQLErrors.length > 0) {
          this.error = error.graphQLErrors[0].message;
        } else {
          this.error = this.$t('triallesson.warnings.unexpectedError');
        }

        return false;
      }
    }
  }
};
</script>
